<script setup>
import { ref, computed, h, onMounted, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useOperators } from 'dashboard/components-next/filter/operators';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  generateAutomationPayload,
  getAttributes,
  getFileName,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useSupportCasesStore } from 'dashboard/stores/supportCases';
import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  SUPPORT_CASE_EVENT_NAMES,
} from './constants';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['save']);
const automation = defineModel('automation', { type: Object, default: null });

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  comma_separated_plain_text: 'plainText',
  date: 'date',
};

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { operators } = useOperators();

const dialogRef = ref(null);
const conditionsRef = useTemplateRef('conditionsRef');
const errors = ref({});

const isEditMode = computed(() => props.mode === 'edit');

const titleKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.TITLE' : 'AUTOMATION.ADD.TITLE'
);
const cancelKey = computed(() =>
  isEditMode.value
    ? 'AUTOMATION.EDIT.CANCEL_BUTTON_TEXT'
    : 'AUTOMATION.ADD.CANCEL_BUTTON_TEXT'
);
const submitKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.SUBMIT' : 'AUTOMATION.ADD.SUBMIT'
);

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(
        attribute.key
      );
    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);

const filterTypes = computed(() => {
  const event = eventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);

  return attributes.map(attr => {
    if (attr.disabled) {
      return { value: attr.key, label: attr.name, disabled: true };
    }

    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-violet-11' }),
      };
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: attr.name,
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  });
});

// A gated event is absent rather than present-and-disabled, for the same reason as the
// gated conditions: offering an event that can never fire builds a rule the workspace
// believes is covering them when it is not.
const automationRuleEvents = computed(() =>
  AUTOMATION_RULE_EVENTS.filter(
    event => !event.featureFlag || isCloudFeatureEnabled(event.featureFlag)
  ).map(event => ({
    ...event,
    value: t(`AUTOMATION.EVENTS.${event.value}`),
  }))
);

const hasAutomationMutated = computed(() => {
  return Boolean(
    automation.value?.conditions[0]?.values ||
      automation.value?.actions[0]?.action_params?.length
  );
});

const store = useStore();
const appIntegrations = useMapGetter('integrations/getAppIntegrations');
const casesStore = useSupportCasesStore();

// The automation settings screen does not otherwise load integrations, and the travel
// action is hidden without them, so they are fetched once when the form opens.
//
// The ticket type catalogue is fetched the same way and for the same reason: the case
// conditions offer types and stages, nothing else on this screen loads them, and
// `fetchCaseTypes` is a no-op once they are in the store.
onMounted(() => {
  if (!appIntegrations.value.length) store.dispatch('integrations/get');
  if (isCloudFeatureEnabled(FEATURE_FLAGS.SUPPORT_CASES)) {
    casesStore.fetchCaseTypes();
  }
});

// Travel ACTIONS need more than the feature flag that travel conditions need, and the
// difference is the point: a condition reads state we already store, while this calls the
// provider. Offering it to an account with no enabled GDS connection builds a rule that
// fails at runtime, and a rule that silently never works is worse than one never offered.
const isTravelConnected = computed(
  () =>
    isCloudFeatureEnabled(FEATURE_FLAGS.TRAVEL) &&
    appIntegrations.value.some(
      integration =>
        integration.id === 'travelport' && !!integration.hooks?.length
    )
);

// Case events accept the actions their own AUTOMATIONS entry lists and nothing else:
// notifications plus the case mutators (T-16); every other action in the catalogue
// mutates a conversation a case event does not have. The conversation events keep the full catalogue - their entries are
// narrower than what has always been offered there (`add_private_note` and
// `change_priority` are offered but unlisted), so narrowing them here would take away
// actions that work.
const allowedActionKeys = computed(() => {
  const event = eventName.value;
  if (!SUPPORT_CASE_EVENT_NAMES.includes(event)) return null;
  return (props.automationTypes[event]?.actions || []).map(
    action => action.key
  );
});

const automationActionTypes = computed(() => {
  const actionTypes = AUTOMATION_ACTION_TYPES.filter(
    ({ key, requiresTravelConnection }) =>
      (key !== 'add_sla' || isCloudFeatureEnabled('sla')) &&
      (!requiresTravelConnection || isTravelConnected.value) &&
      (!allowedActionKeys.value || allowedActionKeys.value.includes(key))
  );

  return actionTypes.map(action => ({
    ...action,
    label: t(`AUTOMATION.ACTIONS.${action.label}`),
  }));
});

const hasConditionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('condition_'))
);

const hasActionErrors = computed(() =>
  Object.keys(errors.value).some(key => key.startsWith('action_'))
);

watch(
  () => automation.value,
  () => {
    if (Object.keys(errors.value).length) {
      errors.value = {};
    }
  },
  { deep: true }
);

const isConditionsValid = () => {
  if (!conditionsRef.value) return true;
  return conditionsRef.value.every(condition => condition.validate());
};

const resetValidation = () => {
  errors.value = {};
  conditionsRef.value?.forEach(c => c.resetValidation());
};

const syncCustomAttributeTypes = () => {
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(
      ft => ft.attributeKey === condition.attribute_key
    );
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard'
        ? ''
        : filterType?.attributeModel || '';
  });
};

const open = () => {
  resetValidation();
  dialogRef.value?.open();
};

const close = () => {
  resetValidation();
  dialogRef.value?.close();
};

const emitSaveAutomation = () => {
  syncCustomAttributeTypes();
  const conditionsValid = isConditionsValid();
  errors.value = validateAutomation(automation.value);
  if (Object.keys(errors.value).length === 0 && conditionsValid) {
    const payload = generateAutomationPayload(automation.value);
    emit('save', payload, props.mode);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    position="top"
    :title="$t(titleKey)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
  >
    <div v-if="automation" class="flex flex-col w-full gap-5">
      <NextInput
        v-model="automation.name"
        :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
        type="text"
        :message="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
        :message-type="errors.name ? 'error' : 'info'"
        :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
      />
      <NextInput
        v-model="automation.description"
        :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
        type="text"
        :message="
          errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
        "
        :message-type="errors.description ? 'error' : 'info'"
        :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
      />
      <div class="flex flex-col min-w-0 gap-1">
        <label
          for="automationEvent"
          class="mb-0.5 text-heading-3 text-n-slate-12"
        >
          {{ $t('AUTOMATION.ADD.FORM.EVENT.LABEL') }}
        </label>
        <div class="relative">
          <select
            id="automationEvent"
            v-model="automation.event_name"
            class="block w-full px-3 py-2.5 text-sm transition-all duration-200 border-0 rounded-lg appearance-none outline outline-1 -outline-offset-1 bg-none bg-n-alpha-black2 text-n-slate-12 !m-0 ltr:pr-10 rtl:pl-10"
            :class="
              errors.event_name
                ? 'outline-n-ruby-8 focus:outline-n-ruby-9'
                : 'outline-n-weak hover:outline-n-slate-6 focus:outline-n-brand-violet'
            "
            @change="onEventChange()"
          >
            <option
              v-for="event in automationRuleEvents"
              :key="event.key"
              :value="event.key"
            >
              {{ event.value }}
            </option>
          </select>
          <span
            class="absolute inset-y-0 flex items-center pointer-events-none ltr:right-3 rtl:left-3"
          >
            <span class="i-lucide-chevron-down size-4 text-n-slate-11" />
          </span>
        </div>
        <p
          v-if="errors.event_name"
          class="mt-1 mb-0 text-label-small text-n-ruby-9"
        >
          {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
        </p>
        <p
          v-if="!isEditMode && hasAutomationMutated"
          class="pt-1 mb-0 text-label-small text-end text-n-amber-11"
        >
          {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
        </p>
      </div>
      <!-- Conditions Start -->
      <section class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <span
            class="grid rounded-md size-6 place-items-center bg-n-violet-3 text-n-violet-11"
          >
            <span class="i-lucide-filter size-3.5" />
          </span>
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.LABEL') }}
          </span>
        </div>
        <ul
          class="grid gap-4 p-3 list-none border border-solid shadow-sm rounded-xl"
          :class="
            hasConditionErrors
              ? 'border-n-ruby-5 bg-n-ruby-2/50'
              : 'border-n-weak dark:border-n-strong bg-n-solid-1'
          "
        >
          <!-- Only the conditionless events can reach zero rows, and for them an empty
               list is a real rule rather than an unfinished one, so it says what that
               rule does instead of pushing the admin to add a condition back. -->
          <li
            v-if="!automation.conditions.length"
            class="text-sm text-n-slate-11"
          >
            {{ $t('AUTOMATION.ADD.FORM.CONDITIONS.EMPTY_STATE') }}
          </li>
          <template v-for="(condition, i) in automation.conditions" :key="i">
            <ConditionRow
              v-if="i === 0"
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:values="automation.conditions[i].values"
              :filter-types="filterTypes"
              :show-query-operator="false"
              @remove="removeFilter(i)"
            />
            <ConditionRow
              v-else
              ref="conditionsRef"
              v-model:attribute-key="automation.conditions[i].attribute_key"
              v-model:filter-operator="automation.conditions[i].filter_operator"
              v-model:query-operator="
                automation.conditions[i - 1].query_operator
              "
              v-model:values="automation.conditions[i].values"
              :filter-types="filterTypes"
              show-query-operator
              @remove="removeFilter(i)"
            />
          </template>
          <div>
            <NextButton
              icon="i-lucide-plus"
              violet
              faded
              sm
              :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
              @click="appendNewCondition"
            />
          </div>
        </ul>
      </section>
      <!-- Conditions End -->
      <!-- Actions Start -->
      <section class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <span
            class="grid rounded-md size-6 place-items-center bg-n-violet-3 text-n-violet-11"
          >
            <span class="i-lucide-zap size-3.5" />
          </span>
          <span class="text-heading-3 text-n-slate-12">
            {{ $t('AUTOMATION.ADD.FORM.ACTIONS.LABEL') }}
          </span>
        </div>
        <ul
          class="grid p-3 list-none border border-solid shadow-sm rounded-xl"
          :class="
            hasActionErrors
              ? 'border-n-ruby-5 bg-n-ruby-2/50'
              : 'border-n-weak dark:border-n-strong bg-n-solid-1'
          "
        >
          <AutomationActionInput
            v-for="(action, i) in automation.actions"
            :key="i"
            v-model="automation.actions[i]"
            :action-types="automationActionTypes"
            dropdown-max-height="max-h-[7.5rem]"
            :dropdown-values="getActionDropdownValues(action.action_name)"
            :show-action-input="
              showActionInput(automationActionTypes, action.action_name)
            "
            :error-message="
              errors[`action_${i}`]
                ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                : ''
            "
            :initial-file-name="
              isEditMode ? getFileName(action, automation.files) : ''
            "
            @reset-action="resetAction(i)"
            @remove-action="removeAction(i)"
          />
          <div class="pt-2">
            <NextButton
              icon="i-lucide-plus"
              violet
              faded
              sm
              :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
              @click="appendNewAction"
            />
          </div>
        </ul>
      </section>
      <!-- Actions End -->
      <div
        class="flex flex-row justify-end w-full gap-2 pt-4 mt-3 border-t border-n-weak"
      >
        <NextButton
          faded
          slate
          type="reset"
          :label="$t(cancelKey)"
          @click.prevent="close"
        />
        <NextButton
          solid
          violet
          type="submit"
          :label="$t(submitKey)"
          @click="emitSaveAutomation"
        />
      </div>
    </div>
  </Dialog>
</template>
